
import { supabase } from "@/integrations/supabase/client";

// Check if username is already taken
export const checkUsernameExists = async (username: string) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('username')
      .eq('username', username)
      .single();
    
    if (error && error.code !== 'PGRST116') {
      console.error("Error checking username:", error);
      return false;
    }
    
    return !!data;
  } catch (error) {
    console.error("Error checking username:", error);
    return false;
  }
};

// Check if email is already taken
export const checkEmailExists = async (email: string) => {
  try {
    const { data, error } = await supabase.rpc('check_email_exists', {
      email_to_check: email
    });
    
    if (error) {
      console.error("Error checking email:", error);
      return false;
    }
    
    return data;
  } catch (error) {
    console.error("Error checking email:", error);
    return false;
  }
};

// Calculate password strength with improved validation
export const calculatePasswordStrength = (password: string) => {
  // Returns a score from 0-100
  if (!password) return 0;
  
  let score = 0;
  
  // Basic length check
  if (password.length >= 8) score += 20;
  else if (password.length >= 6) score += 10;
  
  // Check for uppercase letters
  if (/[A-Z]/.test(password)) score += 20;
  
  // Check for lowercase letters
  if (/[a-z]/.test(password)) score += 20;
  
  // Check for numbers
  if (/[0-9]/.test(password)) score += 20;
  
  // Check for special characters
  if (/[^A-Za-z0-9]/.test(password)) score += 20;
  
  // Bonus: Check for mixed character types
  const hasVarietyOfChars = 
    (/[A-Z]/.test(password) && /[a-z]/.test(password)) || 
    (/[A-Za-z]/.test(password) && /[0-9]/.test(password)) ||
    (/[A-Za-z0-9]/.test(password) && /[^A-Za-z0-9]/.test(password));
  
  if (hasVarietyOfChars) score += 10;
  
  // Penalty: Repeated characters
  const repeatedCharsMatch = password.match(/(.)\1{2,}/g);
  if (repeatedCharsMatch) {
    score -= repeatedCharsMatch.length * 5;
  }
  
  // Ensure score is between 0 and 100
  return Math.max(0, Math.min(100, score));
};

// Update user profile in Supabase
export const updateUserProfile = async (userId: string, profileData: {
  username?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  bio?: string;
  preferences?: Record<string, any>;
}) => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        ...profileData,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) {
      console.error("Error updating profile:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error updating profile:", error);
    return false;
  }
};

// Get current user profile
export const getCurrentUserProfile = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return null;
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
      
    if (error) {
      console.error("Error fetching profile:", error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error("Error fetching current user profile:", error);
    return null;
  }
};

// Validate email format
export const isValidEmail = (email: string): boolean => {
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return pattern.test(email);
};

// Validate username
export const isValidUsername = (username: string): boolean => {
  // Alphanumeric and underscore, between 3 and 20 characters
  const pattern = /^[a-zA-Z0-9_]{3,20}$/;
  return pattern.test(username);
};

// Generate random username based on name and random numbers
export const generateUsername = (firstName: string, lastName: string): string => {
  const base = `${firstName.toLowerCase()}${lastName.toLowerCase().substring(0, 2)}`;
  const randomNum = Math.floor(1000 + Math.random() * 9000);
  return `${base}${randomNum}`;
};

// Clean up auth state for reliable login/logout
export const cleanupAuthState = () => {
  // Remove standard auth tokens
  localStorage.removeItem('supabase.auth.token');
  
  // Remove all Supabase auth keys from localStorage
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      localStorage.removeItem(key);
    }
  });
  
  // Remove from sessionStorage if in use
  Object.keys(sessionStorage || {}).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      sessionStorage.removeItem(key);
    }
  });
};
